// ============================================================
// TMR Portal Admin — Crédito → Consulta (rota /credito/consulta) (26 §9)
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Notice } from '../../components/Notice';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Btn } from '../../components/primitives';
import { TextField } from '../../components/fields';
import { PageHeader, Table, Td } from '../../components/ui';
import { EmptyState, ErrorState } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useCreditProfileSearch, useRequestBureauReport } from '../../data/credit/queries';
import { clinicLabel, hasRestriction, maskCpf, reasonLabel } from '../../data/credit/status';
import { formatDay } from '../../data/businessRules/status';
import type { BureauReportView, CreditProfile } from '../../data/credit/contract';
import { BureauConsultDialog } from './BureauConsultDialog';
import { BureauReportCard, DebtsTable, Muted, Section, SituationBadge } from './CreditParts';

/** What the last consult to the Serasa did, said beside the result. */
type Outcome = { tone: 'success' | 'neutral' | 'warning' | 'danger'; text: string };

/**
 * A customer's credit at the TMR, by CPF: the bureau consults, the rating evaluations of the customer's sales and the
 * open installments in any clinic — classified by the TMR's default threshold, since the lookup crosses clinics. The
 * CPF travels in a POST body and in the navigation state, never in the URL (26 §13).
 *
 * Whoever may query the bureau confirms each search in {@link BureauConsultDialog}: it reuses the report of the last 3
 * months, or pays for one — always, when forced. The profile is read after the consult either way, so a refused or a
 * failed consult still shows what the TMR has kept.
 */
export function CreditProfilePage() {
  const canRead = usePermission(PERMISSIONS.creditReviewsRead);
  const canQuery = usePermission(PERMISSIONS.creditBureauQuery);
  const location = useLocation();
  const initial = (location.state as { document?: string } | null)?.document ?? '';
  const [document, setDocument] = useState(maskCpf(initial));
  const search = useCreditProfileSearch();
  const request = useRequestBureauReport();
  const [confirming, setConfirming] = useState<{ digits: string; key: string } | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const searchedOnArrival = useRef(false);

  // Arriving from an evaluation with its CPF: show what is kept at once, once — a navigation never pays a consult.
  useEffect(() => {
    if (!canRead || !initial || searchedOnArrival.current) return;
    searchedOnArrival.current = true;
    search.mutate(initial);
  }, [canRead, initial, search]);

  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState message="Você não tem permissão para consultar a Análise de Aprovação de clientes." />
        </div>
      </div>
    );
  }

  const digits = document.replace(/\D/g, '');
  const busy = search.isPending || request.isPending;

  const start = () => {
    if (digits.length !== 11 || busy) return;
    setOutcome(null);
    if (canQuery) setConfirming({ digits, key: crypto.randomUUID() });
    else search.mutate(digits);
  };

  const consult = async (target: { digits: string; key: string }, force: boolean) => {
    try {
      const answer = await request.mutateAsync({ document: target.digits, idempotencyKey: target.key, force });
      setOutcome(
        answer.reused
          ? { tone: 'neutral', text: `Usada a consulta de ${formatDay(answer.report.consultedAt)}, dos últimos 3 meses: nada foi pago.` }
          : { tone: 'success', text: 'Consulta feita no Serasa. O resultado está abaixo e vale para a próxima venda deste CPF.' },
      );
    } catch (error) {
      // A refusal (no sale at the TMR, consulted minutes ago) is a warning; the bureau failing is an error.
      setOutcome({
        tone: error instanceof ApiError && error.status < 500 ? 'warning' : 'danger',
        text: error instanceof ApiError ? error.detail : 'Não foi possível consultar o Serasa.',
      });
    }
    search.mutate(target.digits);
  };

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Análise de Aprovação"
        title="Consulta"
        desc="A Análise de Aprovação de um cliente na TMR: o Serasa, as avaliações das vendas e as dívidas em qualquer clínica."
      />

      <div>
        <form
          style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
          onSubmit={(event) => {
            event.preventDefault();
            start();
          }}
        >
          <div style={{ width: 240 }}>
            <TextField label="CPF do cliente" value={document} inputMode="numeric" onChange={(value) => setDocument(maskCpf(value))} />
          </div>
          <button type="submit" style={{ display: 'none' }} aria-hidden />
          {/* 10px vertical: the input's 1px border makes it 2px taller than a button with the default padding. */}
          <Btn variant="primary" icon={Icons.search} style={{ padding: '10px 16px' }} onClick={start} disabled={digits.length !== 11 || busy}>
            {busy ? 'Consultando…' : 'Consultar'}
          </Btn>
        </form>
        {outcome && <Notice tone={outcome.tone}>{outcome.text}</Notice>}
      </div>

      {search.isError ? (
        <ErrorState error={search.error} onRetry={() => digits.length === 11 && search.mutate(digits)} />
      ) : search.data ? (
        <ProfileContent key={search.data.document} profile={search.data} />
      ) : !search.isPending ? (
        <div className="pa-card">
          <EmptyState message="Informe o CPF para ver a Análise de Aprovação do cliente." hint="A consulta mostra dívidas em qualquer clínica da TMR." />
        </div>
      ) : null}

      {confirming && (
        <BureauConsultDialog
          document={maskCpf(confirming.digits)}
          onConsult={(force) => consult(confirming, force)}
          onClose={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

/** A report's identity across the legacy's consults and the ones requested here. */
const reportKey = (report: BureauReportView) => `${report.legacyConsultId ?? ''}-${report.reportId ?? ''}-${report.consultedAt}`;

/**
 * The customer's credit, top to bottom: the Serasa — the score and the restrictions of the selected consult, the most
 * recent by default, with the earlier ones to pick from —, the evaluations of the sales, and the debts at the TMR.
 */
export function ProfileContent({ profile }: { profile: CreditProfile }) {
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const reports = profile.bureauReports;
  const selected = reports.find((report) => reportKey(report) === selectedKey) ?? reports[0];

  return (
    <>
      <div style={{ paddingTop: 4, borderTop: '1px solid var(--border-default)' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 22, margin: '16px 0 0', color: 'var(--ink-900)' }}>
          {profile.names[0] ?? `CPF ${profile.document}`}
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--fg-muted)' }}>
          {profile.knownToTmr ? `CPF ${profile.document}` : `CPF ${profile.document} · sem venda nem pedido na TMR`}
          {profile.names.length > 1 && ` · também cadastrado como ${profile.names.slice(1).join(', ')}`}
        </p>
      </div>

      <Section
        title="Serasa"
        aside={
          selected && selected !== reports[0]
            ? `Consulta de ${formatDay(selected.consultedAt)} · há uma mais recente`
            : reports.length
              ? `${reports.length} ${reports.length === 1 ? 'consulta guardada' : 'consultas guardadas'}`
              : undefined
        }
      >
        {!selected ? (
          <Muted>Nenhuma consulta ao Serasa guardada para este CPF.</Muted>
        ) : (
          <>
            <BureauReportCard key={reportKey(selected)} report={selected} />
            {reports.length > 1 && (
              <ReportHistory reports={reports} selected={selected} onSelect={(report) => setSelectedKey(reportKey(report))} />
            )}
          </>
        )}
      </Section>

      <Section title="Avaliações das vendas">
        {profile.evaluations.length === 0 ? (
          <Muted>Nenhuma venda deste CPF passou pelo rating.</Muted>
        ) : (
          <Table dense columns={[{ label: 'Clínica' }, { label: 'Avaliada em' }, { label: 'Motivos' }, { label: 'Situação' }]}>
            {profile.evaluations.map((evaluation) => (
              <tr
                key={evaluation.evaluationId}
                className="pa-row"
                onClick={() => navigate(`/credito/aprovacoes/${evaluation.evaluationId}`)}
                style={{ cursor: 'pointer' }}
              >
                <Td dense>
                  <Link
                    to={`/credito/aprovacoes/${evaluation.evaluationId}`}
                    style={{ color: 'var(--ink-900)', fontWeight: 600, textDecoration: 'none' }}
                  >
                    {clinicLabel(evaluation.clinic)}
                  </Link>
                </Td>
                <Td dense>{formatDay(evaluation.evaluatedAt)}</Td>
                <Td dense style={{ color: 'var(--fg-muted)' }}>
                  {evaluation.reasons.length === 0 ? '—' : evaluation.reasons.map(reasonLabel).join(' · ')}
                </Td>
                <Td dense>
                  <SituationBadge situation={evaluation.situation} />
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Dívidas na TMR" aside={`Inadimplente após ${profile.delinquencyDays} dias do vencimento (padrão da TMR)`}>
        <DebtsTable debts={profile.debts} emptyMessage="Nenhuma parcela em aberto em nenhuma clínica da TMR." />
      </Section>
    </>
  );
}

/** The consults kept for the CPF, newest first — a click shows that one above. */
function ReportHistory({
  reports,
  selected,
  onSelect,
}: {
  reports: BureauReportView[];
  selected: BureauReportView;
  onSelect: (report: BureauReportView) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span className="pa-eyebrow">Histórico de consultas</span>
      <Table
        dense
        columns={[
          { label: 'Consultado em' },
          { label: 'Score', align: 'right' },
          { label: 'Faixa' },
          { label: 'Prob. de inadimplência' },
          { label: 'Restrições' },
        ]}
      >
        {reports.map((report) => {
          const active = report === selected;
          const restrictions = report.negatives
            .filter(hasRestriction)
            .reduce((sum, negative) => sum + (negative.count ?? negative.occurrences.length), 0);
          return (
            <tr
              key={reportKey(report)}
              className="pa-row"
              onClick={() => onSelect(report)}
              aria-selected={active}
              style={{ cursor: 'pointer', background: active ? 'var(--sage-100)' : undefined }}
            >
              <Td dense style={{ fontWeight: active ? 600 : 400 }}>{formatDay(report.consultedAt)}</Td>
              <Td dense align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{report.score ?? '—'}</Td>
              <Td dense>{report.scoreRange ?? '—'}</Td>
              <Td dense>{report.defaultProbability ?? '—'}</Td>
              <Td dense style={{ color: 'var(--fg-muted)' }}>
                {!report.complete ? 'Só cadastro e score' : restrictions === 0 ? 'Nenhuma' : restrictions}
              </Td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}
